import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FileText, ClipboardList, Eye, EyeOff, Bell } from 'lucide-react';
import {
  FormTemplate,
  getFormTemplates,
  deleteFormTemplate,
} from '../../utils/formsApi';
import FormBuilder from './FormBuilder';
import FormTriggerModal from './FormTriggerModal';

const FormTemplatesList: React.FC = () => {
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<FormTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [triggerTemplate, setTriggerTemplate] = useState<FormTemplate | null>(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await getFormTemplates(!showInactive);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [showInactive]);

  const handleDeleteClick = (template: FormTemplate) => {
    setDeletingTemplate(template);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTemplate) return;
    
    setIsDeleting(true);
    try {
      await deleteFormTemplate(deletingTemplate.id);
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = () => {
    setEditingTemplate(null);
    setIsCreating(false);
    fetchTemplates();
  };

  // Show form builder if editing or creating
  if (editingTemplate || isCreating) {
    return (
      <FormBuilder
        template={editingTemplate}
        onSave={handleSave}
        onCancel={() => {
          setEditingTemplate(null);
          setIsCreating(false);
        }}
      />
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">Form Templates</h2>
          <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
            Create and manage forms for customer intake and data collection
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-dark-text-muted cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 text-teal-600 border-gray-300 dark:border-dark-border rounded"
            />
            Show inactive
          </label>
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Create Form
          </button>
        </div>
      </div>

      {/* Templates Table */}
      <div className="w-full bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
            <p className="text-gray-500 dark:text-dark-text-muted mt-4">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">No form templates</h3>
            <p className="text-gray-500 dark:text-dark-text-muted mb-4">
              Create your first form template to start collecting data from customers.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Create Form
            </button>
          </div>
        ) : (
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-bg-tertiary border-b border-gray-200 dark:border-dark-border">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Form Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Fields
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Triggers
                </th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {template.form_type === 'intake' ? (
                        <ClipboardList className="w-5 h-5 text-teal-500 mr-3" />
                      ) : (
                        <FileText className="w-5 h-5 text-gray-400 dark:text-dark-text-muted mr-3" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-dark-text-primary">{template.name}</p>
                        {template.description && (
                          <p className="text-sm text-gray-500 dark:text-dark-text-muted truncate max-w-xs">
                            {template.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                        template.form_type === 'intake'
                          ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-400 border border-teal-200 dark:border-teal-800'
                          : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-800 dark:text-dark-text-secondary'
                      }`}
                    >
                      {template.form_type === 'intake' ? 'Intake' : 'General'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-dark-text-muted">
                    {template.fields.length} field{template.fields.length !== 1 ? 's' : ''}
                  </td>
                  <td className="px-6 py-4">
                    {template.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                        <Eye className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-muted">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-center">
                    <button
                      onClick={() => setTriggerTemplate(template)}
                      className="p-2 text-gray-400 dark:text-dark-text-muted hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded"
                      title="Configure Triggers"
                    >
                      <Bell className="w-4 h-4" />
                    </button>
                  </td>
                  <td className="px-3 py-4 text-center">
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="p-2 text-gray-400 dark:text-dark-text-muted hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </td>
                  <td className="px-3 py-4 text-center">
                    <button
                      onClick={() => handleDeleteClick(template)}
                      className="p-2 text-gray-400 dark:text-dark-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-bg-secondary max-w-md w-full p-6 rounded-lg border border-gray-200 dark:border-dark-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Delete Template</h3>
            </div>
            <p className="text-gray-600 dark:text-dark-text-muted mb-6">
              Are you sure you want to delete "{deletingTemplate.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingTemplate(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trigger Configuration Modal */}
      {triggerTemplate && (
        <FormTriggerModal
          template={triggerTemplate}
          onClose={() => setTriggerTemplate(null)}
        />
      )}
    </div>
  );
};

export default FormTemplatesList;


